/**
 * The resolution tag of a PNG: the `pHYs` chunk that says how big the image is meant to be on
 * paper ("300 DPI" in Word, Illustrator, InDesign and a journal's figure checker).
 *
 * PyMOL writes it itself when cmd.png is given a dpi (layer0/MyPNG.cpp), so a plain ray-traced
 * export arrives tagged. An image that goes through a canvas afterwards - as it does when labels
 * are painted onto the captured frame - comes back from canvas.toBlob() without it, so the tag is
 * put back here.
 */

/* eslint-disable no-bitwise */

const Signature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const MetresPerInch = 0.0254;

let crcTable = null;

function crc32(bytes) {
    if (!crcTable) {
        crcTable = new Int32Array(256);
        for (let n = 0; n < 256; n++) {
            let c = n;
            for (let k = 0; k < 8; k++) c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
            crcTable[n] = c;
        }
    }
    let c = -1;
    for (let i = 0; i < bytes.length; i++) c = crcTable[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
    return (c ^ -1) >>> 0;
}

/** A PNG chunk: length, 4-character type, data, CRC of type + data. */
function pngChunk(type, data) {
    const out = new Uint8Array(12 + data.length);
    const view = new DataView(out.buffer);
    view.setUint32(0, data.length);
    for (let i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
    out.set(data, 8);
    view.setUint32(8 + data.length, crc32(out.subarray(4, 8 + data.length)));
    return out;
}

function physChunk(dotsPerMetre) {
    const data = new Uint8Array(9);
    const view = new DataView(data.buffer);
    view.setUint32(0, dotsPerMetre); // x
    view.setUint32(4, dotsPerMetre); // y
    data[8] = 1; // unit specifier: 1 = the metre (0 would mean "aspect ratio only")
    return pngChunk('pHYs', data);
}

/**
 * The same PNG with its resolution set to `dpi`, replacing any tag it already carries. The chunk
 * goes directly after IHDR, which the format allows wherever an existing one was.
 * @param {Uint8Array} bytes
 * @param {number} dpi
 * @returns {Uint8Array|null} null if `dpi` is not positive or the bytes are not a whole PNG
 */
export function pngWithDPI(bytes, dpi) {
    const dots = Math.round(Number(dpi) / MetresPerInch);
    if (!(dots > 0) || !bytes || bytes.length < 8) return null;
    for (let i = 0; i < 8; i++) if (bytes[i] !== Signature[i]) return null;

    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const parts = [bytes.subarray(0, 8)];
    let tagged = false;
    let at = 8;
    while (at + 8 <= bytes.length) {
        const length = view.getUint32(at);
        const type = String.fromCharCode(...bytes.subarray(at + 4, at + 8));
        const end = at + 12 + length;
        if (end > bytes.length) return null; // truncated: leave it alone
        if (type !== 'pHYs') parts.push(bytes.subarray(at, end));
        if (!tagged && type === 'IHDR') {
            parts.push(physChunk(dots));
            tagged = true;
        }
        at = end;
    }
    if (!tagged) return null; // no header chunk: not a PNG after all

    const total = parts.reduce((n, part) => n + part.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    for (const part of parts) {
        out.set(part, offset);
        offset += part.length;
    }
    return out;
}

/**
 * The same image, tagged with `dpi`. The original blob is returned unchanged when it is not a PNG,
 * when `dpi` is not positive, or when anything goes wrong: the pixels matter, the tag is a bonus.
 * @param {Blob} blob
 * @param {number} dpi
 * @returns {Promise<Blob>}
 */
export async function setPngDPI(blob, dpi) {
    if (!blob || !(Number(dpi) > 0) || typeof blob.arrayBuffer !== 'function') return blob;
    try {
        const tagged = pngWithDPI(new Uint8Array(await blob.arrayBuffer()), dpi);
        return tagged ? new Blob([tagged], { type: blob.type || 'image/png' }) : blob;
    } catch (ex) {
        console.warn('PyMOL backend: could not set the image resolution', ex);
        return blob;
    }
}
